import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  getObjectId,
  Person,
  PersonEntity,
  personSelector,
  PersonType,
  personTypes,
  useGetPersonQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {PersonForm, PersonFormDefaultValues, PersonFormValues} from "../../components/form/PersonForm/PersonForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {usePersonMutation} from "../../hooks/usePersonMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: PersonFormDefaultValues
}

export const PersonFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <PersonFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromPerson = (person: PersonEntity | undefined): PersonFormDefaultValues => {
  return {
    type: person?.type,
    firstName: person?.firstName,
    lastName: person?.lastName,
    barcode: person?.barcode,
    company: person?.companyId,
    username: person?.userName,
    passwordSet: person?.passwordSet,
    emailAddress: person?.emailAddress,
    phoneNumber: person?.phoneNumber,
    preferredLanguage: person?.preferredLanguage,
    doNotUse: person?.doNotUse,
  };
};

export const PersonFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);
  const {hasAccess} = useAuth()

  const selectPerson = useMemo(() => {
    return personSelector()
  }, [])

  const {currPerson, isLoading} = useGetPersonQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      currPerson: selectPerson(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: PersonFormDefaultValues = useMemo(() => {
    const defaults = formDefaults ? formDefaults : toFormDefaultsFromPerson(currPerson);
    return copy ? {...defaults, password: undefined, passwordSet: false} : defaults;
  }, [currPerson, copy, formDefaults]);

  const title: string = useMemo(() => {
    if (currPerson && !copy) {
      return t("page.entity.person.form.title.edit", "Edit User", {ns: "common"});
    } else {
      return t("page.entity.person.form.title.new", "New User", {ns: "common"});
    }
  }, [currPerson, copy, t]);

  const editablePersonTypes = useMemo(() => {
    const types: PersonType[] = []

    if (hasAccess('person_edit')) {
      for (const type of personTypes) {
        if (hasAccess(`person_${type.code.toLowerCase()}_edit`)) {
          types.push(type)
        }
      }
    }

    return types
  }, [hasAccess]);

  const {
    createPerson,
    updatePerson,
    isCreating,
    isUpdating,
  } = usePersonMutation()

  const onPersonSubmit = useCallback((formValues: PersonFormValues) => {
    const patch: Partial<Person> = {
      type: formValues.type.code,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      barcode: formValues.barcode,
      companyId: getObjectId(formValues.company),
      userName: formValues.username,
      password: formValues.passwordChanged ? formValues.password : undefined,
      passwordChanged: formValues.passwordChanged,
      emailAddress: formValues.emailAddress,
      phoneNumber: formValues.phoneNumber,
      preferredLanguage: formValues.preferredLanguage?.code,
      doNotUse: formValues.doNotUse,
    }

    if (currPerson && !copy) {
      const person: Partial<Person> = {
        ...currPerson.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updatePerson(person, { onSuccess: () => navigate(-1) })
    } else {
      const person: Partial<Person> = {
        ...patch,
      };
      createPerson(person, { onSuccess: (payload) => navigate(`/persons/profile?id=${payload.id}`, {replace: true}) })
    }
  }, [currPerson, copy, createPerson, updatePerson, navigate]);

  if (currPerson && !copy) {
    if (editablePersonTypes.findIndex(t => t === currPerson.type) === -1) {
      return <ErrorPage message={t<string>("common.error.unauthorized", "Unauthorized")}/>
    }
  }

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <PersonForm
                  options={{
                    regionId: regionId,
                    typeDisabled: (!copy && typeof currPerson !== 'undefined'),
                    types: editablePersonTypes,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onPersonSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

